import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { BadgeCheck, Loader2, Plus, Quote as QuoteIcon, Star, X, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "./Reveal";
import api, { formatApiError } from "../lib/api";

const Stars = ({ rating, className = "h-4 w-4" }) => (
  <div className="flex gap-0.5" aria-label={`${rating} out of 5`}>
    {[1, 2, 3, 4, 5].map((s) => (
      <Star key={s} className={`${className} ${s <= rating ? "fill-flame text-flame" : "text-foreground/20"}`} />
    ))}
  </div>
);

const VoicesSection = () => {
  const [reviews, setReviews] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [author, setAuthor] = useState("");
  const [role, setRole] = useState("");
  const [text, setText] = useState("");
  const [rating, setRating] = useState(5);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api
      .get("/reviews")
      .then((r) => setReviews(r.data))
      .catch(() => setReviews([]));
  }, []);

  const submit = async () => {
    if (!author.trim() || !text.trim()) {
      toast.error("Add your name and a line or two first.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/reviews", { author, role, text, rating });
      setReviews((rs) => [data, ...(rs || [])]);
      toast.success("Thanks for adding your voice!");
      setModalOpen(false);
      setAuthor("");
      setRole("");
      setText("");
      setRating(5);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="voices" className="relative py-16 md:py-24 bg-paper/60 dark:bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Eyebrow>Voices & quotes</Eyebrow>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter">
              What people told me
            </h2>
            <p className="mt-3 text-muted-foreground max-w-2xl">
              Real voices from around Morbi — teens, parents, coaches. Write yours below: keep it
              healthy, kind and football-related.
            </p>
            <p className="mt-2 inline-flex items-center gap-2 text-xs font-semibold text-pitch dark:text-turf">
              <ShieldCheck className="h-4 w-4" /> Bad words (even sneaky ones like a**) are
              blocked automatically.
            </p>
          </div>
          <button
            data-testid="add-quote-btn"
            onClick={() => setModalOpen(true)}
            className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-flame text-white font-semibold hover:brightness-110 hover:-translate-y-0.5 transition-all shrink-0"
          >
            <Plus className="h-4 w-4" /> Add your voice
          </button>
        </Reveal>

        {reviews === null ? (
          <div className="mt-10 grid place-items-center py-16 text-muted-foreground" data-testid="reviews-loading">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : reviews.length === 0 ? (
          <div className="mt-10 rounded-3xl border-2 border-dashed border-foreground/15 p-10 text-center" data-testid="reviews-empty">
            <QuoteIcon className="h-8 w-8 mx-auto text-muted-foreground" />
            <p className="mt-3 font-display font-bold text-lg">The wall is waiting for its first voice</p>
            <p className="text-sm text-muted-foreground mt-1">Yours could be it — hit "Add your voice".</p>
          </div>
        ) : (
          <div className="mt-10 columns-1 sm:columns-2 lg:columns-3 gap-6">
            {reviews.map((r, idx) => (
              <motion.div
                key={r.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.55, delay: 0.04 * Math.min(idx, 5) }}
                className="break-inside-avoid mb-6"
              >
                <div data-testid="review-quote-card" className="rounded-3xl bg-card border border-foreground/10 p-6 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between">
                    <QuoteIcon className="h-6 w-6 text-flame" />
                    <Stars rating={r.rating} />
                  </div>
                  <p className="mt-4 font-serif italic text-lg leading-snug">"{r.text}"</p>
                  <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <p className="font-semibold text-sm">{r.author}</p>
                      {r.role && <p className="text-xs text-muted-foreground">{r.role}</p>}
                    </div>
                    {r.source === "owner" ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-turf/15 text-pitch dark:text-turf px-3 py-1 font-mono text-[10px] uppercase tracking-[0.14em] font-semibold">
                        <BadgeCheck className="h-3.5 w-3.5" /> Told to me
                      </span>
                    ) : (
                      <span className="rounded-full bg-muted px-3 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                        From the community
                      </span>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-[60] grid place-items-center p-4" data-testid="submit-quote-modal">
          <div className="absolute inset-0 bg-pitch-deep/60 backdrop-blur-sm" onClick={() => setModalOpen(false)} aria-hidden="true" />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-lg rounded-3xl bg-card border border-foreground/10 p-6 md:p-8 shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-label="Add your voice"
          >
            <button onClick={() => setModalOpen(false)} aria-label="Close" className="absolute top-4 right-4 h-9 w-9 grid place-items-center rounded-full hover:bg-muted transition-colors">
              <X className="h-4 w-4" />
            </button>
            <h3 className="font-display text-2xl font-bold tracking-tight">Your side of the story</h3>
            <p className="mt-1.5 text-sm text-muted-foreground">
              Why do you watch, play, or both? Healthy and kind only — bad words (even a**-style
              ones) get bounced.
            </p>
            <div className="mt-5 space-y-4">
              <input
                data-testid="quote-input-author"
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                placeholder="Your name or a nickname"
                className="w-full h-12 rounded-2xl border-2 border-foreground/10 bg-background px-4 text-sm focus:outline-none focus:border-pitch dark:focus:border-turf"
              />
              <input
                data-testid="quote-input-role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                placeholder="Who are you? (e.g. local teen, parent, coach — optional)"
                className="w-full h-12 rounded-2xl border-2 border-foreground/10 bg-background px-4 text-sm focus:outline-none focus:border-pitch dark:focus:border-turf"
              />
              <textarea
                data-testid="quote-input-content"
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={4}
                placeholder="Your quote…"
                className="w-full rounded-2xl border-2 border-foreground/10 bg-background px-4 py-3 text-sm focus:outline-none focus:border-pitch dark:focus:border-turf resize-y"
              />
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium mr-1">Rating:</span>
                {[1, 2, 3, 4, 5].map((s) => (
                  <button key={s} onClick={() => setRating(s)} aria-label={`Rate ${s} stars`} data-testid={`quote-star-${s}`}>
                    <Star className={`h-6 w-6 transition-transform hover:scale-110 ${s <= rating ? "fill-flame text-flame" : "text-foreground/20"}`} />
                  </button>
                ))}
              </div>
              <button
                data-testid="quote-submit-btn"
                onClick={submit}
                disabled={submitting}
                className="w-full h-12 rounded-full bg-pitch text-cream font-semibold hover:bg-pitch-deep disabled:opacity-50 transition-colors dark:bg-turf dark:text-background"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin inline" /> : "Send my quote"}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </section>
  );
};

export default VoicesSection;
